import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Download, Send, Loader2, FileText, ShieldCheck, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import {
    generatePropertyVendorReport,
    previewPropertyVendorReport,
} from '../../../features/analytics/api/analyticsApi';

const PERIOD_LABELS = { 7: 'Last 7 days', 30: 'Last 30 days', all: 'Full campaign' };

// Fallback list if the preview call fails, so the modal still works. Mirrors
// VENDOR_SECTIONS / DEFAULT_VENDOR_SECTIONS in property_campaign_analytics.py.
const FALLBACK_SECTIONS = [
    { key: 'cover', label: 'Front cover page' },
    { key: 'health', label: 'Campaign health score' },
    { key: 'narrative', label: 'Campaign summary in words' },
    { key: 'activity', label: 'Campaign activity numbers' },
    { key: 'movement', label: 'Movement against last week' },
    { key: 'funnel', label: 'How buyers moved through the campaign' },
    { key: 'top_activity', label: 'Strongest buyer activity' },
    { key: 'open_homes', label: 'Open homes' },
    { key: 'price_feedback', label: 'Open home pricing feedback graph' },
    { key: 'buyer_comments', label: 'Buyer comments from the open homes' },
    { key: 'benchmark', label: 'How this campaign compares' },
    { key: 'commentary', label: 'Your commentary on the market response' },
    { key: 'signature', label: 'Your contact details' },
];
const FALLBACK_DEFAULTS = FALLBACK_SECTIONS.map((s) => s.key);

// Section key -> the row bucket the agent can prune line by line.
const ROW_SECTIONS = {
    activity: 'tiles',
    movement: 'movement',
    funnel: 'funnel',
    top_activity: 'top_activity',
    open_homes: 'open_homes',
    benchmark: 'benchmark',
    buyer_comments: 'buyer_comments',
};
const EMPTY_HIDDEN = {
    tiles: [], movement: [], funnel: [], top_activity: [], open_homes: [], benchmark: [],
    price_feedback: [], buyer_comments: [],
};

function downloadBase64Pdf(filename, base64) {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'campaign-report.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 4000);
}

const Tick = ({ checked, onChange, label, hint, strong = false }) => (
    <label className="flex cursor-pointer select-none items-start gap-2">
        <input
            type="checkbox"
            checked={checked}
            onChange={onChange}
            className="mt-0.5 h-3.5 w-3.5 rounded border-gray-300 text-[#55C5D0] focus:ring-[#55C5D0]"
        />
        <span className={`text-xs ${strong ? 'font-semibold text-gray-800' : 'text-gray-600'}`}>
            <span>{label}</span>
            {hint ? <span className="ml-1 text-gray-400">{hint}</span> : null}
        </span>
    </label>
);

/**
 * Generate the vendor-facing campaign report: download it, or email it straight
 * to the owner. Buyer names never appear in the vendor copy.
 *
 * The agent edits the document before the PDF is made (CG, 13 Aug 2026, "make
 * this doc editable before creating the PDF, like we did for the buyer match
 * PDF"): every section is a tick box, the rows inside them can be pruned one by
 * one, and the campaign summary paragraph the vendor reads is editable text. The
 * tick boxes come from a preview call, so they list exactly what this campaign
 * produced for the chosen period.
 */
export default function VendorReportModal({ propertyId, defaultPeriod = '30', ownerEmail = '', ownerName = '', narrative = '', onClose }) {
    const [period, setPeriod] = useState(defaultPeriod);
    const [commentary, setCommentary] = useState('');
    const [emailTo, setEmailTo] = useState(ownerEmail || '');
    const [preparedFor, setPreparedFor] = useState(ownerName || '');
    const [busy, setBusy] = useState('');

    const [options, setOptions] = useState(null);
    const [loadingOptions, setLoadingOptions] = useState(true);
    const [sections, setSections] = useState(() => new Set(FALLBACK_DEFAULTS));
    // Rows the agent has UNticked, per bucket.
    const [hidden, setHidden] = useState(() => ({ ...EMPTY_HIDDEN }));
    // The paragraph the vendor reads. Generated by the backend, editable here.
    const [summaryText, setSummaryText] = useState(narrative || '');
    const [generatedSummary, setGeneratedSummary] = useState(narrative || '');
    // The pricing feedback graph, read out of the open home notes and editable
    // column by column (CG, 18 Aug 2026: "This graph could also be manually
    // edited before generating the report").
    const [priceChart, setPriceChart] = useState([]);
    const [generatedChart, setGeneratedChart] = useState([]);
    // Buyer comments are anonymous in the owner's copy unless the agent says
    // otherwise for this one report.
    const [showBuyerNames, setShowBuyerNames] = useState(false);

    // Re-read the options whenever the period changes: a 7-day report contains
    // different rows from a full-campaign one.
    useEffect(() => {
        let cancelled = false;
        setLoadingOptions(true);
        previewPropertyVendorReport(propertyId, period)
            .then((data) => {
                if (cancelled) return;
                setOptions(data);
                setSections(new Set(data.default_sections || FALLBACK_DEFAULTS));
                setHidden({ ...EMPTY_HIDDEN });
                setGeneratedSummary(data.narrative || '');
                setPriceChart((data.price_chart || []).map((bar) => ({ ...bar })));
                setGeneratedChart((data.price_chart || []).map((bar) => ({ ...bar })));
                // Keep the agent's own wording if they've already edited it.
                setSummaryText((current) => (
                    !current || current === generatedSummary ? (data.narrative || '') : current
                ));
            })
            .catch(() => { /* keep the fallback list; generating still works */ })
            .finally(() => { if (!cancelled) setLoadingOptions(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propertyId, period]);

    const sectionList = options?.sections?.length ? options.sections : FALLBACK_SECTIONS;

    const toggleSection = useCallback((key) => {
        setSections((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    }, []);

    const toggleRow = useCallback((bucket, key) => {
        setHidden((prev) => {
            const current = prev[bucket] || [];
            return {
                ...prev,
                [bucket]: current.includes(key)
                    ? current.filter((k) => k !== key)
                    : [...current, key],
            };
        });
    }, []);

    const summaryEdited = summaryText.trim() !== (generatedSummary || '').trim();

    const setBar = (index, patch) => setPriceChart((prev) => prev.map(
        (bar, i) => (i === index ? { ...bar, ...patch } : bar),
    ));
    const removeBar = (index) => setPriceChart((prev) => prev.filter((_, i) => i !== index));
    const addBar = () => setPriceChart((prev) => [
        ...prev, { key: `bar-${prev.length + 1}`, label: '', count: 0 },
    ]);
    const resetChart = () => setPriceChart(generatedChart.map((bar) => ({ ...bar })));

    const chartEdited = JSON.stringify(priceChart) !== JSON.stringify(generatedChart);
    const tallestBar = Math.max(1, ...priceChart.map((bar) => Number(bar.count) || 0));
    const chartRows = priceChart.filter((bar) => (bar.label || '').trim());

    const payload = useMemo(() => ({
        period,
        commentary: commentary.trim(),
        narrative: sections.has('narrative') && summaryEdited ? summaryText.trim() : '',
        preparedFor: preparedFor.trim(),
        // An agent who deletes every column has taken the graph out, so the
        // section goes with it rather than the backend rebuilding it from the notes.
        sections: Array.from(sections).filter(
            (key) => !(key === 'price_feedback' && chartEdited && !chartRows.length),
        ),
        hidden,
        // Only sent when the agent actually redrew it: otherwise the backend
        // reads the graph out of the notes itself.
        priceChart: sections.has('price_feedback') && chartEdited ? chartRows : null,
        showBuyerNames: sections.has('buyer_comments') && showBuyerNames,
    }), [period, commentary, sections, summaryEdited, summaryText, preparedFor, hidden,
        chartEdited, chartRows, showBuyerNames]);

    const run = async (mode) => {
        setBusy(mode);
        try {
            const data = await generatePropertyVendorReport(propertyId, {
                ...payload,
                emailTo: mode === 'email' ? emailTo.trim() : '',
            });
            if (mode === 'email') {
                toast.success(`Report emailed to ${data.recipient}`);
                onClose();
            } else {
                downloadBase64Pdf(data.filename, data.pdf_base64);
                toast.success('Vendor report downloaded');
            }
        } catch (error) {
            const message = error?.response?.data?.error || 'Could not generate the vendor report.';
            toast.error(message);
        } finally {
            setBusy('');
        }
    };

    // The rows inside a section, shown indented under it.
    const renderRows = (sectionKey) => {
        const bucket = ROW_SECTIONS[sectionKey];
        if (!bucket || !sections.has(sectionKey)) return null;
        const rows = options?.rows?.[bucket] || [];
        if (!rows.length) return null;
        return (
            <div className="ml-5 mt-1.5 grid grid-cols-1 gap-x-4 gap-y-1 border-l border-gray-100 pl-3 sm:grid-cols-2">
                {rows.map((row) => (
                    <Tick
                        key={row.key}
                        checked={!(hidden[bucket] || []).includes(row.key)}
                        onChange={() => toggleRow(bucket, row.key)}
                        label={row.label}
                        hint={row.count === null || row.count === undefined ? '' : `· ${row.count}`}
                    />
                ))}
            </div>
        );
    };

    return createPortal(
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
            <div
                className="flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-5 py-4">
                    <h3 className="flex items-center gap-2 text-base font-bold text-[#374151]">
                        <FileText size={18} className="text-[#55C5D0]" /> Generate vendor report
                    </h3>
                    <button type="button" onClick={onClose} className="rounded-lg p-1 hover:bg-gray-100">
                        <X size={18} className="text-gray-500" />
                    </button>
                </div>

                <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
                    <div>
                        <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-gray-500">
                            Reporting period
                        </label>
                        <div className="flex gap-1.5">
                            {Object.entries(PERIOD_LABELS).map(([value, label]) => (
                                <button
                                    key={value}
                                    type="button"
                                    onClick={() => setPeriod(value)}
                                    className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${
                                        period === value
                                            ? 'bg-[#374151] text-white'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* What goes in the PDF. Ticked = shown. */}
                    <div className="rounded-xl border border-gray-200 p-3">
                        <div className="mb-2 flex items-center justify-between">
                            <span className="text-xs font-bold uppercase tracking-wide text-gray-500">
                                What to include
                            </span>
                            {loadingOptions && <Loader2 size={14} className="animate-spin text-gray-400" />}
                        </div>
                        <div className="space-y-1.5">
                            {sectionList.map((section) => (
                                <div key={section.key}>
                                    <Tick
                                        strong
                                        checked={sections.has(section.key)}
                                        onChange={() => toggleSection(section.key)}
                                        label={section.label}
                                    />
                                    {renderRows(section.key)}
                                </div>
                            ))}
                        </div>
                        <p className="mt-2 text-[11px] text-gray-400">
                            Untick anything this owner doesn't need. Numbers are for the reporting period
                            you picked above.
                        </p>
                    </div>

                    <div>
                        <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-gray-500">
                            Prepared for
                        </label>
                        <input
                            type="text"
                            value={preparedFor}
                            onChange={(e) => setPreparedFor(e.target.value)}
                            placeholder="Mr & Mrs Smith"
                            className="w-full rounded-xl border border-gray-200 p-2.5 text-sm focus:border-[#55C5D0] focus:outline-none"
                        />
                    </div>

                    {sections.has('narrative') && (
                        <div>
                            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-gray-500">
                                Campaign summary the owner reads
                            </label>
                            <textarea
                                value={summaryText}
                                onChange={(e) => setSummaryText(e.target.value)}
                                rows={4}
                                placeholder="Loading the campaign summary…"
                                className="w-full rounded-xl border border-gray-200 p-3 text-sm focus:border-[#55C5D0] focus:outline-none"
                            />
                            <span className="mt-1 block text-[11px] text-gray-400">
                                Written from this campaign's numbers. Edit it for this report
                                {summaryEdited && generatedSummary ? (
                                    <>
                                        , or
                                        <button
                                            type="button"
                                            onClick={() => setSummaryText(generatedSummary)}
                                            className="ml-1 font-semibold text-[#55C5D0] hover:text-[#47b1bb]"
                                        >
                                            reset to the generated wording
                                        </button>
                                    </>
                                ) : null}.
                            </span>
                        </div>
                    )}

                    {/* The pricing feedback graph, editable column by column. The
                        columns start from the prices the agents wrote in their open
                        home notes, and whatever is left here is what the owner sees. */}
                    {sections.has('price_feedback') && (
                        <div className="rounded-xl border border-gray-200 p-3">
                            <div className="mb-2 flex items-center justify-between">
                                <span className="text-xs font-bold uppercase tracking-wide text-gray-500">
                                    Pricing feedback graph
                                </span>
                                {chartEdited && generatedChart.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={resetChart}
                                        className="text-[11px] font-semibold text-[#55C5D0] hover:text-[#47b1bb]"
                                    >
                                        Reset to your notes
                                    </button>
                                )}
                            </div>

                            {priceChart.length === 0 ? (
                                <p className="text-[11px] text-gray-500">
                                    No prices were found in the open home notes for this period.
                                    Add the columns by hand, or untick this section to leave the
                                    graph out.
                                </p>
                            ) : (
                                <div className="space-y-1.5">
                                    {priceChart.map((bar, index) => (
                                        <div key={bar.key || index} className="flex items-center gap-2">
                                            <input
                                                type="text"
                                                value={bar.label || ''}
                                                onChange={(e) => setBar(index, { label: e.target.value })}
                                                placeholder="$400,000"
                                                aria-label={`Price for column ${index + 1}`}
                                                className="w-28 shrink-0 rounded-lg border border-gray-200 px-2 py-1 text-xs focus:border-[#55C5D0] focus:outline-none"
                                            />
                                            <input
                                                type="number"
                                                min="0"
                                                aria-label={`Buyers at ${bar.label || `column ${index + 1}`}`}
                                                value={bar.count ?? 0}
                                                onChange={(e) => setBar(index, {
                                                    count: Math.max(0, Number(e.target.value) || 0),
                                                })}
                                                className="w-16 shrink-0 rounded-lg border border-gray-200 px-2 py-1 text-xs focus:border-[#55C5D0] focus:outline-none"
                                            />
                                            <div className="h-2 flex-1 rounded bg-gray-100">
                                                <div
                                                    className="h-2 rounded bg-[#55C5D0]"
                                                    style={{ width: `${((Number(bar.count) || 0) / tallestBar) * 100}%` }}
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => removeBar(index)}
                                                className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-red-500"
                                                title="Remove this column"
                                            >
                                                <Trash2 size={13} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <button
                                type="button"
                                onClick={addBar}
                                className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-[#55C5D0] hover:text-[#47b1bb]"
                            >
                                <Plus size={12} /> Add a price
                            </button>
                            <p className="mt-1.5 text-[11px] text-gray-400">
                                Read from the prices your agents wrote in the open home notes. Change
                                the number of buyers at any price, or add one they told you off the
                                record.
                            </p>
                        </div>
                    )}

                    {sections.has('buyer_comments') && (
                        <div className="rounded-xl border border-gray-200 p-3">
                            <Tick
                                strong
                                checked={showBuyerNames}
                                onChange={() => setShowBuyerNames((on) => !on)}
                                label="Show buyers' first names against their comments"
                            />
                            <p className="mt-1.5 text-[11px] text-gray-400">
                                Off by default: the owner reads the comments as Buyer 1, Buyer 2.
                                Only ever a first name, never an email address or a phone number.
                            </p>
                        </div>
                    )}

                    {sections.has('health') && options?.health?.reason && (
                        <div className="rounded-xl bg-[#f0fafb] p-3 text-xs text-[#374151]">
                            <span className="font-bold">
                                Campaign health {options.health.score}/100, {options.health.label}:{' '}
                            </span>
                            {options.health.reason}
                        </div>
                    )}

                    {sections.has('commentary') && (
                        <div>
                            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-gray-500">
                                Your commentary {ownerName ? `for ${ownerName}` : ''}
                            </label>
                            <textarea
                                value={commentary}
                                onChange={(e) => setCommentary(e.target.value)}
                                rows={4}
                                placeholder="Your read on the market response this week: where the interest sits, price feedback, what you recommend next."
                                className="w-full rounded-xl border border-gray-200 p-3 text-sm focus:border-[#55C5D0] focus:outline-none"
                            />
                        </div>
                    )}

                    <div>
                        <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-gray-500">
                            Email it to the owner
                        </label>
                        <input
                            type="email"
                            value={emailTo}
                            onChange={(e) => setEmailTo(e.target.value)}
                            placeholder="owner@example.com"
                            className="w-full rounded-xl border border-gray-200 p-2.5 text-sm focus:border-[#55C5D0] focus:outline-none"
                        />
                    </div>

                    <p className="flex items-start gap-1.5 text-[11px] text-gray-500">
                        <ShieldCheck size={13} className="mt-0.5 shrink-0 text-emerald-600" />
                        Buyer names, emails and phone numbers are never included in the vendor copy.
                    </p>
                </div>

                <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-gray-200 px-5 py-4">
                    <button
                        type="button"
                        onClick={() => run('download')}
                        disabled={!!busy}
                        className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-[#374151] hover:bg-gray-50 disabled:opacity-50"
                    >
                        {busy === 'download' ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
                        Download PDF
                    </button>
                    <button
                        type="button"
                        onClick={() => run('email')}
                        disabled={!!busy || !emailTo.trim()}
                        className="inline-flex items-center gap-1.5 rounded-xl bg-[#55C5D0] px-4 py-2 text-sm font-bold text-white hover:bg-[#47b1bb] disabled:opacity-50"
                    >
                        {busy === 'email' ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                        Email to owner
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
}
